// src/domains/table.ts —— 06 C-00 通用表格机制原生实现（TD-820 服务端分页 / facet / 排序 / 请求串回显；TD-821 / TD-846 服务端 CSV 与失败映射；TD-849 页文案接线）
// 已注册页（pages/index 的 PageDef）由原生生成全部表格 / 抽屉 / 表单 / 确认框键；非表格页（dashboard / settings / upstream / roles）表格区为空表，
// 其余未注册页属装配错误：dev 抛错，prod console.error 一次并按空表渲染（TD-824 / TD-825 影子实例已退场）。
// 会话边界：每个 host 一份 HostRun（TableState 集合 + 导出状态），session:lost / session:ready 各整组替换并复位 tb 切片；
// 旧 HostRun 的在途结果（列表 / 导出 / 表单提交 / 行动作）在写入前按 HostRun 同一性丢弃，不串到下一账号。
import { ApiError } from '../../../shared/src/api/client';
import { isNotReady, notReady } from '../../../shared/src/api/pending';
import type { AdminPage } from '../../../shared/src/api/types';
import type { I18nPack } from '../../../shared/src/dc';
import { csvRowCount, fallbackCsvName, saveBlob } from '../../../shared/src/download';
import { listQuery, type ListQuery } from '../../../shared/src/page';
import { TableState } from '../../../shared/src/table';
import { todayIn } from '../../../shared/src/time';
import { I18N_TABLE, REWRITES_TABLE } from '../i18n/table';
import { ALL_PAGES, pageDefOf } from '../pages/index';
import type { ConfirmSpec, DrawerSpec, ErasedView, FieldDef, FormChange, FormDef, FormVals, Need, PageDef, RowGate } from '../pages/types';
import { act, btn, dtabStyle, field, li, pageBtnStyle, statCard, subStyleOf, thStyle } from '../pages/ui';
import { PAGE_MODULE, SUPER_ADMIN_ROLE, can, gate, gatedStyle, type Gate } from '../perm';
import { tableDefaults, type Page, type TableSlice } from '../state';
import { KEYS } from './table.keys';
import { str, type AdminCtx, type AdminDomain, type InputEvent } from './types';

export interface MergedPacks {
  i18n: I18nPack;
  rewrites: readonly string[];
}

// TD-849：页文件文案包并入 table 域字典。同引用去重；不同包同键 → dev 抛错 / prod console.error 一次并保留先到者
export function mergePagePacks(base: MergedPacks, defs: ReadonlyArray<PageDef | undefined>, dev: boolean): MergedPacks {
  const zh: Record<string, unknown> = { ...base.i18n.zh };
  const en: Record<string, unknown> = { ...base.i18n.en };
  const owner = new Map<string, string>([...Object.keys(zh), ...Object.keys(en)].map((k) => [k, 'table'] as const));
  const seen = new Set<I18nPack>([base.i18n]);
  const rewrites = new Set<string>(base.rewrites);
  let reported = false;
  for (const d of defs) {
    if (!d?.i18n) continue;
    for (const k of d.rewrites ?? []) rewrites.add(k);
    if (seen.has(d.i18n)) continue;
    seen.add(d.i18n);
    for (const k of new Set([...Object.keys(d.i18n.zh), ...Object.keys(d.i18n.en)])) {
      const prev = owner.get(k);
      if (prev !== undefined) {
        const msg = `页文案键冲突：${k}（${prev} / ${d.page}）`;
        if (dev) throw new Error(msg);
        if (!reported) {
          reported = true;
          console.error('[gp:i18n]', msg);
        }
        continue;
      }
      owner.set(k, d.page);
      if (k in d.i18n.zh) zh[k] = d.i18n.zh[k];
      if (k in d.i18n.en) en[k] = d.i18n.en[k];
    }
  }
  return { i18n: { zh, en }, rewrites: [...rewrites] };
}

// 惰性合并：页文件 import table.ts（openForm 等），table.ts 又经 pages/index 引用页文件，形成加载环。
// 以 pages/index 或页文件为入口时，本模块求值期 ALL_PAGES 尚未初始化（ESM TDZ），故求值期不得读取；首次读取字典时再合并并缓存
let packsCache: MergedPacks | null = null;

export function pagePacks(): MergedPacks {
  if (!packsCache) packsCache = mergePagePacks({ i18n: I18N_TABLE, rewrites: REWRITES_TABLE }, Object.values(ALL_PAGES), import.meta.env.DEV);
  return packsCache;
}

// ---- 会话代（每 host 一份；替换即换代） ----

interface HostRun {
  ready: boolean; // session:ready 之后才发列表请求（受限窗口 / 登录页不触达业务端点）
  readonly tables: Map<string, TableState<unknown>>;
  readonly lastGood: WeakMap<TableState<unknown>, AdminPage<unknown>>;
  readonly warned: Set<string>; // TD-846：导出未就绪告警每路由每会话一次
  exporting: boolean;
}

const runs = new WeakMap<object, HostRun>();
const unsubs = new WeakMap<object, ReadonlyArray<() => void>>();

function freshRun(ready: boolean): HostRun {
  return { ready, tables: new Map(), lastGood: new WeakMap(), warned: new Set(), exporting: false };
}

// 未经 mount 的宿主（域单测直接构造的 AdminCtx）视为已就绪；产品宿主由 mount 置为未就绪，等 session:ready
function runOf(ctx: AdminCtx): HostRun {
  let r = runs.get(ctx.host);
  if (!r) {
    r = freshRun(true);
    runs.set(ctx.host, r);
  }
  return r;
}

// 会话守卫：调用时刻的 HostRun 仍是当前 HostRun 才返回 true（session:lost / session:ready 之后即失效）
export function sessionGuard(ctx: AdminCtx): () => boolean {
  const run = runOf(ctx);
  const host = ctx.host;
  return () => runs.get(host) === run;
}

// 抽屉离场钩子（2026-10-17 后台卡面明文查看）：某个抽屉 spec 被替换 / 关闭 / 会话复位时同步回调一次，
// 入参 = 接替它的抽屉（关闭为 null）。供页面在抽屉离场时立即丢弃内存中的敏感数据；不进 DrawerSpec 形状（契约锁）。
const drawerLeave = new WeakMap<DrawerSpec, (next: DrawerSpec | null) => void>();

export function onDrawerLeave(d: DrawerSpec, fn: (next: DrawerSpec | null) => void): void {
  drawerLeave.set(d, fn);
}

function leaveDrawer(prev: DrawerSpec | null, next: DrawerSpec | null): void {
  if (prev === null || prev === next) return;
  const fn = drawerLeave.get(prev);
  if (!fn) return;
  drawerLeave.delete(prev);
  fn(next);
}

function dropSession(ctx: AdminCtx, ready: boolean): void {
  runs.set(ctx.host, freshRun(ready));
  const tb = ctx.host.state.tb;
  if (tb.drawer !== null || tb.form !== null || tb.confirm !== null || tb.note !== '' || tb.rev !== 0) ctx.host.setState({ tb: tableDefaults() });
  leaveDrawer(tb.drawer, null);
}

function setTb(ctx: AdminCtx, patch: Partial<TableSlice>): void {
  const prev = ctx.host.state.tb.drawer;
  ctx.host.setState({ tb: { ...ctx.host.state.tb, ...patch } });
  if ('drawer' in patch) leaveDrawer(prev, patch.drawer ?? null);
}

// 行动作 / 主操作 / 抽屉按钮：同步抛错与异步拒绝统一 toast（ui.err）；会话已换则静默
function runSafe(ctx: AdminCtx, fn: () => void | Promise<void>, done?: () => void): void {
  const alive = sessionGuard(ctx);
  void Promise.resolve()
    .then(fn)
    .catch((e: unknown) => {
      if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
      else console.warn('[gp:table] 会话已结束，丢弃动作结果', e);
    })
    .finally(() => done?.());
}

// 写动作在途防重：同一行对象（或同一抽屉）× 同一动作在途期间重复点击不再发起（双击重复审核 / 结算 → 1012 / 1501 紧随成功 toast）。
// 键为行 / 抽屉对象同一性：列表重载后为新对象，自然解锁；动作结束（成功或失败）即释放
const inflight = new WeakMap<object, Set<string>>();

function runLocked(ctx: AdminCtx, owner: unknown, label: string, fn: () => void | Promise<void>): void {
  if (typeof owner !== 'object' || owner === null) return runSafe(ctx, fn);
  let set = inflight.get(owner);
  if (!set) {
    set = new Set();
    inflight.set(owner, set);
  }
  if (set.has(label)) return;
  const held = set;
  held.add(label);
  runSafe(ctx, fn, () => held.delete(label));
}

// ---- 视图与表格状态 ----

export function viewOf(ctx: AdminCtx): ErasedView | null {
  const s = ctx.host.state;
  const def = pageDefOf(s.page);
  return def ? def.views[s.tab] ?? def.views[0] ?? null : null;
}

function emptyPage(q: ListQuery): AdminPage<unknown> {
  return { list: [], total: 0, page: q.page, size: q.size };
}

function asPage(r: AdminPage<unknown>, q: ListQuery): AdminPage<unknown> {
  // 信封边界只做最小形状兜底：list 非数组时按空页渲染，避免整页渲染抛错
  return Array.isArray(r.list) ? r : { ...emptyPage(q), total: typeof r.total === 'number' ? r.total : 0 };
}

export function tableOf(ctx: AdminCtx, page: Page, v: ErasedView): TableState<unknown> {
  const run = runOf(ctx);
  const id = `${page}:${v.key}`;
  const hit = run.tables.get(id);
  if (hit) return hit;
  const host = ctx.host;
  const ts: TableState<unknown> = new TableState<unknown>({
    endpoint: v.echo,
    minLoadingMs: 420, // 原型 L1020 load()
    searchingMs: 520, // 原型 L945
    fetch: (q) => fetchView(ctx, run, ts, v, q),
    onChange: () => {
      if (runs.get(host) === run) setTb(ctx, { rev: host.state.tb.rev + 1 });
    },
  });
  ts.tab = v.key;
  if(page==='cryptoPayments')ts.pageSize=20;
  run.tables.set(id, ts);
  return ts;
}

export function toQuery(v: ErasedView, q: ListQuery): Record<string, string | number> {
  const src = v.source;
  if ('pending' in src) return {};
  const { tab, ...rest } = q;
  const base: ListQuery = { ...rest, ...(v.noDate ? { start: '', end: '' } : {}), ...(v.noKeyword ? { keyword: '' } : {}) };
  if (src.tabParam === 'tab') return listQuery({ ...base, ...(tab ? { tab } : {}) });
  if (src.tabParam === 'status') return listQuery({ ...base, facets: { ...(base.facets ?? {}), status: tab === 'all' ? '' : tab ?? '' } });
  const fixed = tab && src.tabFacet ? src.tabFacet[tab] ?? {} : {};
  return listQuery({ ...base, facets: { ...(base.facets ?? {}), ...fixed } });
}

// 回显串与实际下发参数同源（tab / status / 固定 facet / sort / order；noDate / noKeyword 视图不含被置空的参数）
function echoLine(v: ErasedView, q: ListQuery): string {
  const qs = Object.entries(toQuery(v, q)).map(([k, x]) => `${k}=${encodeURIComponent(String(x))}`);
  return `GET ${v.echo}?${qs.join('&')}`;
}

async function fetchView(ctx: AdminCtx, run: HostRun, ts: TableState<unknown>, v: ErasedView, q: ListQuery): Promise<AdminPage<unknown>> {
  const src = v.source;
  if ('pending' in src) {
    // D8-8 搭壳：告警一次（shared notReady 去重）并按空页渲染，不进 [gp:table] 错误日志
    try {
      return await notReady<AdminPage<unknown>>(src.pending);
    } catch {
      return emptyPage(q);
    }
  }
  try {
    const r = asPage(await ctx.gp.api.call<AdminPage<unknown>>(src.route, { query: toQuery(v, q) }), q);
    if (runs.get(ctx.host) === run) run.lastGood.set(ts, r);
    return r;
  } catch (e) {
    if (runs.get(ctx.host) !== run) return emptyPage(q); // 旧会话实例不可达，空页只为让其 reload 收尾
    // C-00：网络 / 5xx / 403 → toast，保留上一次成功数据
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
    const prev = run.lastGood.get(ts);
    if (prev) return prev;
    throw e;
  }
}

// 导航 / 页签切换：查询条件、请求串、排序清空，page=1（C-00 / 原型 L682 nav.go）
function resetTo(ts: TableState<unknown>): Promise<void> {
  ts.sortKey = null;
  ts.sortDir = 'desc';
  return ts.setTab(ts.tab);
}

export async function exportCsv(ctx: AdminCtx): Promise<void> {
  const v = viewOf(ctx);
  if (!v || 'pending' in v.source) return;
  const route = v.source.route;
  const run = runOf(ctx);
  if (run.exporting) return;
  const alive = sessionGuard(ctx);
  const ts = tableOf(ctx, ctx.host.state.page, v);
  run.exporting = true;
  try {
    const { blob, filename } = await ctx.gp.api.blob(route, { query: { ...toQuery(v, { ...ts.query(), page: 1 }), format: 'csv' } });
    if (!alive()) return;
    const n = csvRowCount(await blob.text());
    saveBlob(blob, filename ?? fallbackCsvName('goodpayu', v.csvName, todayIn(ctx.host.state.sh.timezone || undefined)));
    ctx.gp.ui.toast(`${str(ctx.t, 'toastExport')} · ${n} rows`);
  } catch (e) {
    if (!alive()) return;
    // TD-846：4220 field=format（该列表未声明导出）视为接口未就绪；5000 为服务端错误，走 ui.err
    if (e instanceof ApiError && e.code === 4220 && e.data.field === 'format') {
      const k = `${route}?format=csv`;
      if (!run.warned.has(k)) {
        run.warned.add(k);
        console.warn('[gp:not-ready]', k);
      }
      ctx.gp.ui.toast(str(ctx.t, 'featurePending'));
      return;
    }
    // 1501 → toastNoPerm（gp.api 拦截已触发 /me 刷新）；1504 → errExportLimit；429 / 1002 → err1002 插值 retry_after
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
  } finally {
    run.exporting = false;
  }
}

export function openDrawer(ctx: AdminCtx, d: DrawerSpec): void {
  setTb(ctx, { drawer: d, note: '' });
}

export function closeDrawer(ctx: AdminCtx): void {
  setTb(ctx, { drawer: null });
}

export function openForm(ctx: AdminCtx, def: FormDef): void {
  setTb(ctx, { form: { def, vals: { ...def.init }, err: '', saving: false } });
}

export function openConfirm(ctx: AdminCtx, c: ConfirmSpec): void {
  setTb(ctx, { confirm: c });
}

type FormSlice = NonNullable<TableSlice['form']>;

// 只改同一张表单（def 引用相同）：提交在途期间表单被关闭或换成另一张时不串写
function patchForm(ctx: AdminCtx, def: FormDef, p: Partial<FormSlice>): void {
  const cur = ctx.host.state.tb.form;
  if (cur && cur.def === def) setTb(ctx, { form: { ...cur, ...p } });
}

function setFormVal(ctx: AdminCtx, k: string, v: string | boolean): void {
  const cur = ctx.host.state.tb.form;
  if (!cur || cur.saving) return;
  const vals = { ...cur.vals, [k]: v };
  setTb(ctx, { form: { ...cur, vals } });
  const hook = cur.def.onChange;
  if (!hook) return;
  const def = cur.def;
  // 只合并到同一张、未在提交的表单；异步结果另要求触发字段值未再变化（旧响应不覆盖新输入）
  const apply = (ch: FormChange | void, async: boolean): void => {
    if (!ch) return;
    const now = ctx.host.state.tb.form;
    if (!now || now.def !== def || now.saving || (async && now.vals[k] !== v)) return;
    setTb(ctx, {
      form: {
        ...now, vals: { ...now.vals, ...(ch.vals ?? {}) }, ...(ch.err !== undefined ? { err: ch.err } : {}),
        ...(ch.options ? { opts: { ...(now.opts ?? {}), ...ch.options } } : {}),
      },
    });
  };
  let out: FormChange | Promise<FormChange> | void;
  try {
    out = hook(ctx, k, vals);
  } catch (e) {
    console.warn('[gp:admin] form onChange', k, e);
    return;
  }
  if (out instanceof Promise) {
    out.then(
      (ch) => apply(ch, true),
      (e: unknown) => console.warn('[gp:admin] form onChange', k, e),
    );
  } else apply(out, false);
}

// file 字段（0321 EV-1）：取出选中文件、清空控件（同一文件可再次选择），交给 def.onFiles；结果只合并到同一张未在提交的表单
interface FileEvent {
  target: { files?: ArrayLike<File> | null; value?: string };
}
function pickFormFiles(ctx: AdminCtx, k: string, e: FileEvent): void {
  const cur = ctx.host.state.tb.form;
  const files = Array.from(e.target.files ?? []);
  e.target.value = '';
  const hook = cur?.def.onFiles;
  if (!cur || cur.saving || !hook || files.length === 0) return;
  const def = cur.def;
  hook(ctx, k, files, cur.vals).then(
    (ch) => {
      const now = ctx.host.state.tb.form;
      if (!now || now.def !== def || now.saving) return;
      setTb(ctx, { form: { ...now, vals: { ...now.vals, ...(ch.vals ?? {}) }, ...(ch.err !== undefined ? { err: ch.err } : {}) } });
    },
    (err: unknown) => console.warn('[gp:admin] form onFiles', k, err),
  );
}

export async function submitForm(ctx: AdminCtx): Promise<void> {
  const f = ctx.host.state.tb.form;
  const t = ctx.t;
  if (!f || f.saving) return;
  const def = f.def;
  // C-00：除 switch / readonly 与 optional 外均必填；红条「请填写必填项：{字段列表}」（原型 L1025–1026 口径）
  const miss = def.fields.filter((fl) => fl.type !== 'switch' && fl.type !== 'readonly' && !fl.optional && (f.vals[fl.k] === '' || f.vals[fl.k] === undefined));
  if (miss.length) return patchForm(ctx, def, { err: `${str(t, 'required')}： ${miss.map((fl) => fl.label).join('  ')}` });
  const ve = def.validate?.(f.vals, t) ?? '';
  if (ve) return patchForm(ctx, def, { err: ve });
  const alive = sessionGuard(ctx);
  patchForm(ctx, def, { saving: true, err: '' });
  try {
    await def.submit(ctx, f.vals);
    // submit 内部可能结束会话（改密 relogin）：会话已换时 tb 已由 dropSession 复位，不再写入
    if (!alive()) return;
    if (ctx.host.state.tb.form?.def === def) setTb(ctx, { form: null });
  } catch (e) {
    if (!alive()) return;
    patchForm(ctx, def, { saving: false, err: ctx.gp.ui.err(e) });
  }
}

export async function doConfirm(ctx: AdminCtx): Promise<void> {
  const c = ctx.host.state.tb.confirm;
  if (!c) return;
  setTb(ctx, { confirm: null });
  const alive = sessionGuard(ctx);
  try {
    await c.run(ctx);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

export function reloadCurrent(ctx: AdminCtx): Promise<void> {
  const v = viewOf(ctx);
  return v ? tableOf(ctx, ctx.host.state.page, v).reload() : Promise.resolve();
}

// ---- renderVals ----

const NO_GATE: Gate = { disabled: false, title: '' };
// 原型 L126 主操作 / L125 导出按钮样式串逐字（置灰经 gatedStyle 追加）
const PRIMARY_STYLE = 'height:36px;padding:0 14px;border:0;border-radius:10px;background:#0f1a17;color:#fff;font-weight:700;font-size:13px;cursor:pointer';
const SECONDARY_STYLE = 'height:36px;padding:0 14px;border:1px solid #e6ebe8;border-radius:10px;background:#fff;font-weight:700;font-size:13px;cursor:pointer';
const noop = (): void => undefined;

// 文案键或 `~` 前缀的已本地化文本（pages/types ActionDef.label 约定）；未登记键回落键名
function labelOf(ctx: AdminCtx, l: string): string {
  if (l.startsWith('~')) return l.slice(1);
  return str(ctx.t, l) || l;
}

function gateOf(ctx: AdminCtx, need: Need | undefined): Gate {
  return need ? gate(ctx.s.sh.me, ctx.t, need.module, need.bit) : NO_GATE;
}

// 权限位置灰优先；权限足够时再看业务置灰（ActionDef.gate / DrawerAction.gate）
function rowGateOf(ctx: AdminCtx, need: Need | undefined, biz: (() => RowGate) | undefined): Gate | undefined {
  const p = need ? gateOf(ctx, need) : undefined;
  if (p?.disabled) return p;
  const b = biz?.();
  return b?.disabled ? b : p;
}

function canExportPage(ctx: AdminCtx, page: Page): boolean {
  const me = ctx.s.sh.me;
  const m = PAGE_MODULE[page];
  if (m === 'open') return !!me;
  if (m === 'super') return me?.role === SUPER_ADMIN_ROLE;
  return can(me, m, 'export');
}

function pagerStyle(enabled: boolean): string {
  // 原型 L951–952 prev / next 样式串逐字
  return `height:28px;padding:0 10px;border:1px solid #e6ebe8;border-radius:7px;background:#fff;font-size:12px;cursor:${enabled ? 'pointer' : 'not-allowed'};opacity:${enabled ? 1 : 0.5}`;
}

function gridVals(ctx: AdminCtx, page: Page, v: ErasedView): Record<string, unknown> {
  const { t, lang } = ctx;
  const ts = tableOf(ctx, page, v);
  const run = runOf(ctx);
  const lastPage: AdminPage<unknown> & Record<string, unknown> = { ...(run.lastGood.get(ts) ?? emptyPage(ts.query())) };
  const stats = v.stats?.(ctx, lastPage) ?? [];
  const note = v.note?.(t) ?? '';
  const lastReq = ts.lastReq ? echoLine(v, ts.query()) : '';
  const p = v.primary;
  const pg = gateOf(ctx, p?.need);
  const sec = v.secondary;
  const sg = gateOf(ctx, sec?.need);
  return {
    cols: v.cols.map((c) => {
      const sortable = !!c.sortKey;
      const active = sortable && ts.sortKey === c.sortKey;
      const arrow = sortable ? (active ? (ts.sortDir === 'desc' ? ' ↓' : ' ↑') : ' ⇅') : '';
      return {
        label: str(t, c.label) + (c.unit ? ` (${c.unit})` : '') + arrow,
        sortable,
        notSortable: !sortable,
        // 02 §2.2：白名单外 sort 服务端 422，非白名单列不挂排序
        sort: () => {
          if (c.sortKey) void ts.toggleSort(c.sortKey);
        },
        style: thStyle(active, sortable),
      };
    }),
    rows: ts.rows.map((r) => ({
      cells: v.cells(ctx, r).map((c) => ({ ...c, subStyle: subStyleOf(c) })),
      open: () => v.open?.(ctx, r),
      actions: (v.rowActions?.(ctx, r) ?? [])
        .filter((a) => a.show?.(r) ?? true)
        .map((a) => {
          const g = a.gate;
          return act(labelOf(ctx, a.label), () => runLocked(ctx, r, a.label, () => a.run(ctx, r)), a.danger ?? false, rowGateOf(ctx, a.need, g ? () => g(ctx, r) : undefined));
        }),
    })),
    rowsEmpty: ts.rows.length === 0 && !ts.loading,
    rowCountText: `${ts.total} ${lang === 'zh' ? '条记录' : 'records'} · ${lang === 'zh' ? '第' : 'page'} ${ts.pageNo}/${ts.totalPages}`,
    pageSize: String(ts.pageSize),
    onPageSize: (e: InputEvent) => void ts.setPageSize(Number.parseInt(e.target.value, 10)),
    pages: ts.pages.map((n) => ({ n, go: () => void ts.goPage(n), style: pageBtnStyle(n === ts.pageNo) })),
    prevPage: () => {
      if (ts.pageNo > 1) void ts.goPage(ts.pageNo - 1);
    },
    nextPage: () => {
      if (ts.pageNo < ts.totalPages) void ts.goPage(ts.pageNo + 1);
    },
    prevStyle: pagerStyle(ts.pageNo > 1),
    nextStyle: pagerStyle(ts.pageNo < ts.totalPages),
    // noDate / noKeyword 视图的输入保留渲染（原型 1:1），值不进请求（toQuery 置空）
    qd: { kw: ts.draft.kw, from: ts.draft.from, to: ts.draft.to },
    onQd: {
      kw: (e: InputEvent) => ts.setDraft({ kw: e.target.value }),
      from: (e: InputEvent) => ts.setDraft({ from: e.target.value }),
      to: (e: InputEvent) => ts.setDraft({ to: e.target.value }),
    },
    // P-12：选项 = 服务端 facet_options（与当前页数据无关），下拉恒显示；首项「全部」由模板 L111 给出
    facets: v.cols
      .filter((c) => !!c.facet)
      .map((c) => {
        const param = c.facet ?? '';
        return {
          label: str(t, c.label),
          param,
          value: ts.draft.f[param] ?? '',
          options: (ts.facetOptions[param] ?? []).map((x) => ({ v: x, label: c.facetLabel?.(t, x) ?? x })),
          onChange: (e: InputEvent) => ts.setDraft({ f: { [param]: e.target.value } }),
        };
      }),
    runQuery: () => void ts.runQuery(),
    resetQuery: () => void ts.resetQuery(),
    searching: ts.searching,
    loading: ts.loading,
    lastReq,
    hasLastReq: !!lastReq,
    noLastReq: !lastReq,
    tableBodyStyle: `overflow-x:auto;transition:opacity .2s;opacity:${ts.loading ? 0.45 : 1}`, // 原型 L931
    hasStats: stats.length > 0,
    stats: stats.map((x) => statCard(x.label, x.value, x.sub, x.dark ?? false, x.danger ?? false)),
    pageNote: note,
    hasPageNote: note !== '',
    primaryAction: p ? labelOf(ctx, p.label) : '',
    primaryIcon: p?.icon ?? '+ ',
    doPrimary: () => {
      if (p && !pg.disabled) runSafe(ctx, () => p.run(ctx));
    },
    primaryTitle: pg.title,
    primaryStyle: gatedStyle(PRIMARY_STYLE, pg),
    secondaryAction: sec ? labelOf(ctx, sec.label) : false,
    secondaryIcon: sec?.icon ?? '',
    doSecondary: () => {
      if (sec && !sg.disabled) runSafe(ctx, () => sec.run(ctx));
    },
    secondaryTitle: sg.title,
    secondaryStyle: gatedStyle(SECONDARY_STYLE, sg),
    exportCsv: () => void exportCsv(ctx),
    // G-10 第 3 条：无 export 位隐藏；搭壳视图（pending）与 noExport 视图（06 C-15）无导出端点
    canExport: 'route' in v.source && !v.noExport && canExportPage(ctx, page),
  };
}

// 已注册但无视图（空页定义）：表格主体按空表渲染
function idleGrid(): Record<string, unknown> {
  return {
    cols: [], rows: [], rowsEmpty: true, rowCountText: '', pageSize: '10', onPageSize: noop, pages: [], prevPage: noop, nextPage: noop, prevStyle: pagerStyle(false), nextStyle: pagerStyle(false),
    qd: { kw: '', from: '', to: '' }, onQd: { kw: noop, from: noop, to: noop }, facets: [], runQuery: noop, resetQuery: noop, searching: false, loading: false,
    lastReq: '', hasLastReq: false, noLastReq: true, tableBodyStyle: 'overflow-x:auto;transition:opacity .2s;opacity:1', hasStats: false, stats: [], pageNote: '', hasPageNote: false,
    primaryAction: '', primaryIcon: '+ ', doPrimary: noop, primaryTitle: '', primaryStyle: PRIMARY_STYLE,
    secondaryAction: false, secondaryIcon: '', doSecondary: noop, secondaryTitle: '', secondaryStyle: SECONDARY_STYLE, exportCsv: noop, canExport: false,
  };
}

function drawerVals(ctx: AdminCtx, d: DrawerSpec): Record<string, unknown> {
  const t = ctx.t;
  const tabs = d.tabs;
  return {
    drawerOpen: true,
    closeDrawer: () => closeDrawer(ctx),
    stop: (e: { stopPropagation(): void }) => e.stopPropagation(),
    note: ctx.s.tb.note,
    onNote: (e: InputEvent) => setTb(ctx, { note: e.target.value }),
    // 原型 L965 结构逐键
    drawer: {
      kind: d.kind,
      title: d.title,
      subtitle: d.subtitle ?? '',
      hasTabs: !!tabs,
      noTabs: !tabs,
      tabs: tabs ? tabs.labels.map((label, i) => ({ label, pick: () => tabs.pick(ctx, i), style: dtabStyle(tabs.active === i) })) : [],
      card: !!d.card,
      cardStyle: d.card?.style ?? '',
      cardBalance: d.card?.balance ?? '',
      cardBrand: d.card?.brand ?? '',
      cardNo: d.card?.no ?? '',
      hasFields: (d.fields ?? []).length > 0,
      fields: (d.fields ?? []).map((f) => field(f.k, f.v, f.wide ?? false)),
      hasList: !!d.list,
      listTitle: d.list?.title ?? '',
      list: (d.list?.rows ?? []).map((r) => {
        const go = r.go;
        return li(r.left, r.sub ?? '', r.right ?? '', r.rightSub ?? '', r.tone ?? '', go ? () => go(ctx) : undefined);
      }),
      listEmpty: !!d.list && d.list.rows.length === 0,
      hasJson: d.json !== undefined,
      json: d.json ?? '',
      jsonTitle: d.jsonTitle ?? str(t, 'rawPayload'),
      hasTimeline: (d.timeline ?? []).length > 0,
      timeline: d.timeline ?? [],
      hasNote: !!d.note,
      actions: d.actions.map((a) => {
        const g = a.gate;
        return btn(labelOf(ctx, a.label), () => runLocked(ctx, d, a.label, () => a.run(ctx)), a.kind, rowGateOf(ctx, a.need, g ? () => g(ctx) : undefined));
      }),
    },
  };
}

function drawerClosed(ctx: AdminCtx): Record<string, unknown> {
  return {
    drawerOpen: false,
    closeDrawer: () => closeDrawer(ctx),
    stop: (e: { stopPropagation(): void }) => e.stopPropagation(),
    note: ctx.s.tb.note,
    onNote: (e: InputEvent) => setTb(ctx, { note: e.target.value }),
    drawer: {
      kind: '', title: '', subtitle: '', hasTabs: false, noTabs: true, tabs: [], card: false, cardStyle: '', cardBalance: '', cardBrand: '', cardNo: '', hasFields: false, fields: [],
      hasList: false, listTitle: '', list: [], listEmpty: false, hasJson: false, json: '', jsonTitle: str(ctx.t, 'rawPayload'), hasTimeline: false, timeline: [], hasNote: false, actions: [],
    },
  };
}

function fieldVals(ctx: AdminCtx, fl: FieldDef, vals: FormVals, opts?: Readonly<Record<string, readonly string[]>>): Record<string, unknown> {
  const t = ctx.t;
  const raw = vals[fl.k];
  const on = raw === true;
  // 原型 L874–879 字段结构逐键；readonly 类型 → isRo（0321 X-13 偏差块），isInput 置 false
  return {
    k: fl.k,
    label: fl.label,
    type: fl.type,
    wide: !!fl.wide,
    isSelect: fl.type === 'select',
    isSwitch: fl.type === 'switch',
    isPassword: fl.type === 'password',
    isInput: fl.type === 'text' || fl.type === 'number',
    isRo: fl.type === 'readonly',
    isFile: fl.type === 'file',
    onFile: (e: FileEvent) => pickFormFiles(ctx, fl.k, e),
    value: typeof raw === 'string' ? raw : '',
    placeholder: fl.placeholder ?? '',
    wrapStyle: `display:flex;flex-direction:column;gap:6px;${fl.wide ? 'grid-column:1/-1' : ''}`,
    onChange: (e: InputEvent) => {
      if (fl.type !== 'readonly') setFormVal(ctx, fl.k, e.target.value);
    },
    toggle: () => {
      if (fl.type === 'switch') setFormVal(ctx, fl.k, ctx.host.state.tb.form?.vals[fl.k] !== true);
    },
    switchText: str(t, on ? 'stOn' : 'stOff'),
    switchStyle: `height:44px;border:1px solid ${on ? '#0d9a68' : '#dfe5e2'};border-radius:10px;background:${on ? '#e3f6ee' : '#fff'};color:${on ? '#0d9a68' : '#6b7772'};font-weight:700;font-size:13px;cursor:pointer`,
    options: (opts?.[fl.k] ?? fl.options ?? []).map((o) => ({ v: o, label: fl.optionLabel?.(t, o) ?? o })),
  };
}

function formVals(ctx: AdminCtx, f: FormSlice): Record<string, unknown> {
  return {
    formOpen: true,
    formErr: f.err,
    formSaving: f.saving,
    closeForm: () => setTb(ctx, { form: null }),
    submitForm: () => void submitForm(ctx),
    form: { title: f.def.title, fields: f.def.fields.map((fl) => fieldVals(ctx, fl, f.vals, f.opts)), hasTip: !!f.def.tip, tip: f.def.tip ?? '' },
  };
}

const FORM_CLOSED: Readonly<Record<string, unknown>> = {
  formOpen: false, formErr: '', formSaving: false, closeForm: noop, submitForm: noop, form: { title: '', fields: [], hasTip: false, tip: '' },
};

function confirmVals(ctx: AdminCtx, c: ConfirmSpec | null): Record<string, unknown> {
  const t = ctx.t;
  return {
    confirmOpen: c !== null,
    confirmTitle: c?.title || str(t, 'delTitle'),
    confirmText: c?.text || str(t, 'delText'),
    confirmLabel: c?.label ?? '',
    closeConfirm: () => setTb(ctx, { confirm: null }),
    doConfirm: () => void doConfirm(ctx),
  };
}

// 非表格页（06 C-02 / C-14 / C-18 / C-22 / C-33 专属布局）：不注册页定义，表格区按空表渲染（broadcast = 0049 原生整页，dash.broadcast）
const NON_TABLE: ReadonlySet<Page> = new Set<Page>(['dashboard', 'settings', 'upstream', 'roles', 'broadcast']);
const unregisteredReported = new Set<Page>();

// 表格页未注册 = 装配错误（页文件未并入 pages/index）：dev 抛错暴露，prod 每页报错一次并以空表兜底，不让整壳渲染失败
function unregistered(page: Page): void {
  if (NON_TABLE.has(page)) return;
  const msg = `表格页未注册：${page}`;
  if (import.meta.env.DEV) throw new Error(msg);
  if (unregisteredReported.has(page)) return;
  unregisteredReported.add(page);
  console.error('[gp:table]', msg);
}

// 全部键原生给出；抽屉 / 表单 / 确认框按 tb 切片判定（非表格页上也能打开原生表单，如修改密码）
function tableVals(ctx: AdminCtx): Record<string, unknown> {
  const s = ctx.s;
  const def = pageDefOf(s.page);
  if (!def) unregistered(s.page);
  const tb = s.tb;
  const v = def ? def.views[s.tab] ?? def.views[0] ?? null : null;
  const grid = v ? gridVals(ctx, s.page, v) : idleGrid();
  const drawer = tb.drawer ? drawerVals(ctx, tb.drawer) : drawerClosed(ctx);
  const form = tb.form ? formVals(ctx, tb.form) : FORM_CLOSED;
  return { ...grid, ...drawer, ...form, ...confirmVals(ctx, tb.confirm) };
}

export const table: AdminDomain = {
  name: 'table',
  keys: KEYS,
  get i18n(): I18nPack {
    return pagePacks().i18n; // getter：createDcApp 首次 dict() 时才合并（见 pagePacks）
  },
  get rewrites(): readonly string[] {
    return pagePacks().rewrites;
  },
  legacyKeysFor: () => [],
  mount(ctx) {
    unsubs.get(ctx.host)?.forEach((off) => off());
    runs.set(ctx.host, freshRun(false));
    unsubs.set(ctx.host, [
      // 会话边界各复位一次 tb 切片并换代（上一账号的抽屉 / 表单 / 在途结果不进入下一账号）；ready 后按当前页拉第 1 页
      ctx.events.on('session:lost', () => dropSession(ctx, false)),
      ctx.events.on('session:ready', () => {
        dropSession(ctx, true);
        const v = viewOf(ctx);
        if (v) void tableOf(ctx, ctx.host.state.page, v).reload();
      }),
      // 侧栏同页重复点击（外壳发 screen{from===to}）：与切页同口径——关闭抽屉 / 表单 / 确认框，查询条件 / 排序 / 分页复位后重载（原型 L682 / E-10）
      ctx.events.on('screen', (p) => {
        if (p.from !== p.to || !runOf(ctx).ready) return;
        const tb = ctx.host.state.tb;
        if (tb.drawer || tb.form || tb.confirm) setTb(ctx, { drawer: null, form: null, confirm: null });
        const v = viewOf(ctx);
        if (v) void resetTo(tableOf(ctx, ctx.host.state.page, v));
      }),
    ]);
  },
  unmount(ctx) {
    unsubs.get(ctx.host)?.forEach((off) => off());
    unsubs.delete(ctx.host);
    runs.delete(ctx.host);
  },
  update(ctx, prev) {
    const s = ctx.host.state;
    if (prev.page === s.page && prev.tab === s.tab) return;
    if (!runOf(ctx).ready) return;
    // 原型 L682 nav.go 复位 drawer；原生抽屉 / 表单 / 确认框随页切换关闭
    if (prev.page !== s.page && (s.tb.drawer || s.tb.form || s.tb.confirm)) setTb(ctx, { drawer: null, form: null, confirm: null });
    const v = viewOf(ctx);
    if (v) void resetTo(tableOf(ctx, s.page, v));
  },
  vals: (ctx) => tableVals(ctx),
};

export { isNotReady, gate };
